import { PsHttp, PsHttpChannel, PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import { getCurrentLanguage } from '@telecom/shared/utils/language-tool';
import DataLoader from 'dataloader';
import type { PatentApi } from '../patent-api';
import type {
    PatentProfile,
    PatentProfileByIdsRequest,
    PatentProfileByIdsResponse,
    PatentProfileByKeywordRequest,
    PatentProfileByKeywordResponse,
    PatentListRequest,
    PatentCountResponse,
    PatentListResponse,
    PatentListResponseV2,
    PatentListRequestV2,
    PatentListAsyncResponse,
    PatentProfileWithFamilyResponse,
    PatentProfileWithFamily,
} from '../../types';

@injectable()
export class PatentApiServiceImpl extends PsHttpService
    implements
        PatentApi

{
    private _patentProfileLoaders: Map<string, DataLoader<string, PatentProfile | null>>;
    private _patentProfileWithFamilyLoaders: Map<string, DataLoader<string, PatentProfileWithFamily | null>>;

    constructor(httpClient: PsHttp, httpChannelClient: PsHttpChannel) {
        super(httpClient, httpChannelClient);
        this._patentProfileLoaders = new Map();
        this._patentProfileWithFamilyLoaders = new Map();
    }

    /**
     * 获取或创建指定语言的 PatentProfile DataLoader
     * @private
     */
    private _getOrCreatePatentProfileLoader(lang: string): DataLoader<string, PatentProfile | null> {
        if (!this._patentProfileLoaders.has(lang)) {
            const loader = new DataLoader(
                (patentIds: readonly string[]) => this._batchLoadPatentProfiles(patentIds, lang),
                {
                    cache: true,
                    maxBatchSize: 1000,
                    batchScheduleFn: (callback: () => void) => setTimeout(callback, 10),
                },
            );
            this._patentProfileLoaders.set(lang, loader);
        }
        return this._patentProfileLoaders.get(lang)!;
    }

    /**
     * 获取或创建指定语言的 PatentProfileWithFamily DataLoader
     * @private
     */
    private _getOrCreatePatentProfileWithFamilyLoader(lang: string): DataLoader<string, PatentProfileWithFamily | null> {
        if (!this._patentProfileWithFamilyLoaders.has(lang)) {
            const loader = new DataLoader(
                (patentIds: readonly string[]) => this._batchLoadPatentProfilesWithFamily(patentIds, lang),
                {
                    cache: true,
                    maxBatchSize: 1000,
                    batchScheduleFn: (callback: () => void) => setTimeout(callback, 10),
                },
            );
            this._patentProfileWithFamilyLoaders.set(lang, loader);
        }
        return this._patentProfileWithFamilyLoaders.get(lang)!;
    }
    async _getPatentProfileByIds(data: PatentProfileByIdsRequest): Promise<PatentProfileByIdsResponse> {
        const res = await this.post<PatSnapSuccessResponseData<PatentProfileByIdsResponse>>({
            url: 'patent/batch-basic-info',
            data,
        });
        return res.data;
    }

    /**
     * 使用DataLoader获取单个专利详情
     * @param patentId 专利ID
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<PatentProfile | null>
     */
    async getPatentProfile(patentId: string, lang?: string): Promise<PatentProfile | null> {
        const language = lang || getCurrentLanguage().toUpperCase();
        const loader = this._getOrCreatePatentProfileLoader(language);
        return loader.load(patentId);
    }

    /**
     * 使用DataLoader批量获取多个专利详情
     * @param patentIds 专利ID数组
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<Record<string, PatentProfile>>
     */
    async getPatentProfiles(patentIds: string[], lang?: string): Promise<Record<string, PatentProfile>> {
        const language = lang || getCurrentLanguage().toUpperCase();
        const loader = this._getOrCreatePatentProfileLoader(language);
        const results = await loader.loadMany(patentIds);
        const patentMap: Record<string, PatentProfile> = {};

        results.forEach((result: PatentProfile | Error | null, index: number) => {
            if (result instanceof Error) {
                throw result;
            }
            if (result) {
                patentMap[patentIds[index]] = result;
            }
        });

        return patentMap;
    }

    /**
     * 清空DataLoader缓存
     * @param lang 可选，指定清空某个语言的缓存，不指定则清空所有语言
     */
    clearCache(lang?: string): void {
        if (lang) {
            this._patentProfileLoaders.get(lang)?.clearAll();
            this._patentProfileWithFamilyLoaders.get(lang)?.clearAll();
        } else {
            this._patentProfileLoaders.forEach(loader => loader.clearAll());
            this._patentProfileWithFamilyLoaders.forEach(loader => loader.clearAll());
        }
    }

    /**
     * 清空特定专利ID的缓存
     * @param patentId 专利ID
     * @param lang 可选，指定清空某个语言的缓存，不指定则清空所有语言
     */
    clearPatentCache(patentId: string, lang?: string): void {
        if (lang) {
            this._patentProfileLoaders.get(lang)?.clear(patentId);
            this._patentProfileWithFamilyLoaders.get(lang)?.clear(patentId);
        } else {
            this._patentProfileLoaders.forEach(loader => loader.clear(patentId));
            this._patentProfileWithFamilyLoaders.forEach(loader => loader.clear(patentId));
        }
    }

    /**
     * DataLoader批量加载函数
     * @private
     */
    private async _batchLoadPatentProfiles(patentIds: readonly string[], lang: string): Promise<(PatentProfile | null)[]> {
        const response = await this._getPatentProfileByIds({
            patent_ids: [...patentIds],
            lang,
        });

        return patentIds.map(id => response[id] || null);
    }

    /**
     * DataLoader批量加载带家族信息的专利数据
     * @private
     */
    private async _batchLoadPatentProfilesWithFamily(
        patentIds: readonly string[],
        lang: string,
    ): Promise<(PatentProfileWithFamily | null)[]> {
        const response = await this.post<PatSnapSuccessResponseData<PatentProfileWithFamilyResponse>>({
            url: 'patent/batch-basic-info-with-family',
            data: {
                patent_ids: [...patentIds],
                fields: [
                    'TITLE_LANG',
                    'ABST_LANG',
                    'EXDT',
                    'ANCS',
                    'PDF_IMAGE',
                    'ABST_LANGS',
                    'LEGAL_STATUS',
                    'PBD',
                    'APD',
                    'TITLE',
                    'FAMILY_ID',
                    'ABST',
                    'SIMPLE_LEGAL_STATUS',
                    'PN',
                    'ABST_SOURCE',
                    'TITLE_LANGS',
                    'SIMPLE_FAMILY_LIST',
                    'PATENT_ID',
                ],
                lang,
            },
        });

        return patentIds.map(id => response.data[id] || null);
    }

    /**
     * 使用DataLoader获取单个专利详情及同族信息
     * @param patentId 专利ID
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<PatentProfileWithFamily | null>
     */
    async getPatentProfileWithFamily(patentId: string, lang?: string): Promise<PatentProfileWithFamily | null> {
        const language = lang || getCurrentLanguage().toUpperCase();
        const loader = this._getOrCreatePatentProfileWithFamilyLoader(language);
        return loader.load(patentId);
    }

    /**
     * 批量获取专利详情及同族信息
     * @param patentIds 专利ID数组
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<PatentProfileWithFamilyResponse>
     */
    async getPatentProfilesWithFamily(patentIds: string[], lang?: string): Promise<PatentProfileWithFamilyResponse> {
        const language = lang || getCurrentLanguage().toUpperCase();
        const loader = this._getOrCreatePatentProfileWithFamilyLoader(language);

        // 使用DataLoader批量加载
        const results = await loader.loadMany(patentIds);

        // 组装结果
        const profileMap: PatentProfileWithFamilyResponse = {};
        results.forEach((result: PatentProfileWithFamily | Error | null, index: number) => {
            if (result instanceof Error) {
                throw result;
            }
            if (result) {
                profileMap[patentIds[index]] = result;
            }
        });

        return profileMap;
    }

    async getPatentProfileByKeyword(data: PatentProfileByKeywordRequest): Promise<PatentProfileByKeywordResponse> {
        const res = await this.post<PatSnapSuccessResponseData<PatentProfileByKeywordResponse>>({
            url: 'patent/compare-search',
            data,
            headers: {
                'X-Client-Id': import.meta.env.VITE_PASSPORT_CLIENT_ID,
            },
        });
        return res?.data;
    }

    async getPatentCount(data: PatentListRequest): Promise<PatentCountResponse> {
        const result = await this.post<PatSnapSuccessResponseData<PatentCountResponse>>({
            url: 'patent-search/count',
            data,
        });
        return result.data;
    }

    async getPatentIdsByApnos(apnos: string[]): Promise<Record<string, string[]>> {
        const result = await this.post<PatSnapSuccessResponseData<Record<string, string[]>>>({
            url: 'patent/batch-ids-by-apno',
            data: { apnos },
        });
        return result.data;
    }

    /** @deprecated 使用 getPatentListV2 代替
     * 获取专利列表
     * @param data 专利列表查询请求参数
     * @returns 专利列表响应结果
     */
    async getPatentList(data: PatentListRequest): Promise<PatentListResponse> {
        const result = await this.post<PatSnapSuccessResponseData<PatentListResponse>>({
            url: 'patent-search/patents',
            data,
            cache: true,
        });
        return result.data;
    }

    /**
     * 获取异步专利列表
     * @param data 专利列表查询请求参数
     * @returns 专利列表响应结果
     */
    async getPatentListV2(data: PatentListRequestV2): Promise<PatentListResponseV2> {
        // 首次请求
        const result = await this.post<PatSnapSuccessResponseData<PatentListResponseV2>>({
            url: 'patent-search/patents',
            data,
            cache: false,
        });

        // 如果没有 bad_query_job_id，直接返回结果
        if (!result.data.bad_query_job_id) {
            return result.data;
        }

        // 有 bad_query_job_id，需要轮询异步接口
        return this._pollAsyncPatentList(result.data.bad_query_job_id);
    }

    /**
     * 递归轮询异步专利查询结果
     * @param jobId 查询任务ID
     * @returns 专利列表响应结果
     */
    private async _pollAsyncPatentList(jobId: string): Promise<PatentListAsyncResponse> {
        try {
            const result = await this.post<PatSnapSuccessResponseData<PatentListAsyncResponse>>({
                url: 'patent-search/patents/async',
                data: { bad_query_job_id: jobId },
                cache: false,
            });
            if (result.data.status === 'finished') {
                if (result.data.status_code === 200000) {
                    return result.data;
                }
                throw new Error(result.data.status_code.toString());
            }
            // 如果仍然有 bad_query_job_id，继续轮询
            await new Promise(resolve => setTimeout(resolve, 1000)); // 2秒轮询间隔
            return this._pollAsyncPatentList(jobId);
        } catch (error: any) {
            // 如果是超时错误，继续轮询
            if (this._isTimeoutError(error)) {
                console.warn('异步专利查询请求超时，正在重试...', error);
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this._pollAsyncPatentList(jobId);
            } else {
                throw error;
            }
        }
    }

    /**
     * 判断是否为超时错误
     * @param error 错误对象
     * @returns 是否为超时错误
     */
    private _isTimeoutError(error: any): boolean {
        return error?.code === 'ECONNABORTED' ||
               error?.message?.includes('timeout') ||
               error?.name === 'TimeoutError';
    }

    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }
}
